import React, {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { matchPath, NavLink, useLocation } from 'react-router';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

import { Tooltip } from '@zendesk/forethought-elements';
import { SIDEBAR_HIGHLIGHT_COLOR } from '../constants';
import {
  isEmitTrackingEventRoute,
  trackingEventRouteMapping,
} from '../helpers';
import upperFirst from 'lodash/fp/upperFirst';
import { setTimeRange } from 'src/actions/pageSettings/pageSettings';
import { updateQueryString } from 'src/actions/search';
import { setCurrentApplication } from 'src/actions/user';
import { LabelBadge } from 'src/components/label-badge';
import { NotificationBadge } from 'src/components/notification-badge/NotificationBadge';
import { useEmitTrackingEventCallback } from 'src/hooks/hooks';
import { useGenerateSidebarLinkWithSearch } from 'src/hooks/useGenerateSidebarLinkWithSearch';
import { useGetFeatureFlagsQuery } from 'src/services/dashboard-api';
import { useAppDispatch } from 'src/store/hooks';
import { getAppCuesId } from 'src/utils/appCuesUtil';
import { Apps, Routes } from 'src/utils/enums';
import {
  constructTimeRangeQueryParamsRevamped,
  last30DaysTimeRange,
} from 'src/utils/timeRangeHelpers';

interface NavMenuItemProps {
  /** Additional paths that should also mark this nav item as active */
  additionalMatchPaths?: string[];
  badgeLabel?: string | null;
  childHasBadgeLabel?: boolean;
  /**
   * When true, render as a plain `<a href>` instead of a router NavLink so
   * navigation bypasses the SPA router (and its BASE_PATH). Use for links
   * that target a different app served at the same origin (e.g. `/admin`).
   */
  external?: boolean;
  hideLabel?: boolean;
  icon?: string;
  isTopLevel?: boolean;
  minimized?: boolean;
  name: string;
  notificationsBadgeLabel?: string;
  onClick?: (e: React.MouseEvent) => void;
  onHover?: (e: React.MouseEvent) => void;
  /** Called after navigation occurs (used to close minimized flyouts) */
  onNavigate?: () => void;
  path?: string;
  search?: string;
  selected?: boolean;
  /** When true, don't show the hover tooltip for minimized items (used when the flyout is open) */
  suppressTooltip?: boolean;
  tooltip?: string;
}

interface NavItemProps {
  isTopLevel?: boolean;
  minimized?: boolean;
  selected?: boolean;
}

const NavMenuItem: FunctionComponent<
  React.PropsWithChildren<NavMenuItemProps>
> = ({
  additionalMatchPaths,
  badgeLabel,
  childHasBadgeLabel,
  external = false,
  hideLabel = false,
  icon,
  isTopLevel = false,
  minimized = false,
  name,
  notificationsBadgeLabel,
  onClick,
  onHover,
  onNavigate,
  path,
  search = '',
  selected = false,
  suppressTooltip = false,
  tooltip,
}) => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const pathWithSearch = useGenerateSidebarLinkWithSearch({
    path,
    search,
  });
  const emitTrackingEventCallback = useEmitTrackingEventCallback();
  const { data: featureFlagsData } = useGetFeatureFlagsQuery();
  const { feature_flags: featureFlags = [] } = featureFlagsData ?? {};
  const isSolveWidgetEnabled = featureFlags.includes('solve_widget_enabled');

  // Check if this nav item should be active based on additionalMatchPaths
  const isActiveViaAdditionalPaths =
    additionalMatchPaths?.some(additionalPath =>
      matchPath({ end: false, path: additionalPath }, pathname),
    ) ?? false;

  const handleLinkClick = useCallback(
    (e: React.MouseEvent) => {
      if (!path) {
        onClick?.(e);
        return;
      }

      dispatch(setTimeRange(last30DaysTimeRange));

      dispatch(setCurrentApplication(Apps.INSIGHTS, name));
      dispatch(updateQueryString(''));

      isEmitTrackingEventRoute(path) &&
        emitTrackingEventCallback(trackingEventRouteMapping[path ?? '']);
      // Close any open minimized flyout after navigation
      onNavigate?.();
    },
    [path, dispatch, name, emitTrackingEventCallback, onClick, onNavigate],
  );

  const RenderedNode = (
    path ? NavItem : NavItemWithoutLink
  ) as React.ElementType;

  const formattedName = name.split(' ').map(upperFirst).join(' ');

  const isWorkflowBuilder = path === Routes.WORKFLOW_BUILDER;
  const defaultSolveChannel = isSolveWidgetEnabled ? 'widget' : 'email';
  const timeStampQueryParams = constructTimeRangeQueryParamsRevamped(
    last30DaysTimeRange,
    defaultSolveChannel,
  );

  const redirectTo = isWorkflowBuilder
    ? `${path}?${timeStampQueryParams}`
    : `${pathWithSearch}`;

  // External links bypass the SPA router (and BASE_PATH) by rendering as a
  // plain anchor instead of NavLink. `as='a'` swaps the underlying element
  // while keeping the NavItem styles. `href` is the raw path — no basename
  // prepended, no search-param merging.
  const linkProps: Record<string, unknown> = external
    ? { as: 'a', href: path }
    : { to: redirectTo };

  // Determine if this item should show as active
  const shouldShowActive = selected || isActiveViaAdditionalPaths;

  // Detect whether the label is overflowing (truncated) so we only show
  // the styled Tooltip when the text is actually cut off.
  const labelRef = useRef<HTMLLabelElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  useEffect(() => {
    const el = labelRef.current;
    if (!el) {
      setIsOverflowing(false);
      return;
    }

    const check = () => setIsOverflowing(el.scrollWidth > el.clientWidth);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [name, hideLabel]);

  if (minimized) {
    // Wrap the entire clickable element (including the visible icon) with Tooltip
    // so hovering the visible part triggers the tooltip. Previously the Tooltip
    // wrapped only the hidden label, so hover over the icon didn't activate it.
    const node = (
      <RenderedNode
        className={shouldShowActive ? 'active' : undefined}
        data-has-icon={!!icon}
        isTopLevel={isTopLevel}
        minimized={minimized}
        onClick={handleLinkClick}
        onMouseEnter={onHover}
        {...linkProps}
      >
        {icon && hideLabel && !!(badgeLabel || childHasBadgeLabel) && (
          <NewDot />
        )}
        {icon && <img alt={hideLabel ? name : ''} height='20' src={icon} />}
        {/* badges are intentionally not shown inline in the minimized node; the minimized tooltip/flyout will include details */}
      </RenderedNode>
    );

    // If there's a submenu callback (onHover), suppress tooltip; otherwise show it
    if (suppressTooltip || onHover) {
      return node;
    }

    return (
      <Tooltip
        placement='right'
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 4],
                },
              },
            ],
          },
        }}
        tooltipContent={tooltip ?? formattedName}
      >
        {node}
      </Tooltip>
    );
  }

  return (
    <RenderedNode
      className={shouldShowActive ? 'active' : undefined}
      data-has-icon={!!icon}
      isTopLevel={isTopLevel}
      minimized={minimized}
      onClick={handleLinkClick}
      {...linkProps}
    >
      {icon && hideLabel && !!(badgeLabel || childHasBadgeLabel) && <NewDot />}
      {icon && <img alt={hideLabel ? name : ''} height='20' src={icon} />}
      <span
        style={{
          alignItems: 'center',
          display: 'inline-flex',
          flex: 1,
          minWidth: 0,
        }}
      >
        {hideLabel ? (
          <Label
            aria-hidden={hideLabel}
            data-appcues-target={getAppCuesId({
              componentType: 'label',
              featureName: 'sidebar',
              pageName: 'navigation',
              subType: name,
            })}
            hidden={hideLabel}
            ref={labelRef}
            style={{ display: 'inline-block' }}
          >
            {formattedName}
          </Label>
        ) : isOverflowing ? (
          <Tooltip
            placement='right'
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 26],
                    },
                  },
                ],
              },
            }}
            tooltipContent={formattedName}
          >
            <Label
              aria-hidden={hideLabel}
              data-appcues-target={getAppCuesId({
                componentType: 'label',
                featureName: 'sidebar',
                pageName: 'navigation',
                subType: name,
              })}
              hidden={hideLabel}
              ref={labelRef}
              style={{ display: 'inline-block' }}
            >
              {formattedName}
            </Label>
          </Tooltip>
        ) : (
          <Label
            aria-hidden={hideLabel}
            data-appcues-target={getAppCuesId({
              componentType: 'label',
              featureName: 'sidebar',
              pageName: 'navigation',
              subType: name,
            })}
            hidden={hideLabel}
            ref={labelRef}
            style={{ display: 'inline-block' }}
          >
            {formattedName}
          </Label>
        )}
        <Box>
          {badgeLabel && <LabelBadge badgeLabel={badgeLabel} />}
          {notificationsBadgeLabel && (
            <NotificationBadge label={notificationsBadgeLabel} />
          )}
        </Box>
      </span>
    </RenderedNode>
  );
};

const styledBase = `
  --navbar-item-height: 20px;
  display: flex;
  text-decoration: none;
  align-items: center;
  justify-content: space-between;
  color: rgba(255, 255, 255, 0.7);
  opacity: 0.7;
  transition: color var(--duration-fast), opacity var(--duration-fast);
  min-height: var(--navbar-item-height);
  cursor: pointer;
  &:hover {
    color: var(--color-solid-white);
    background-color: #193D50;
    opacity: 1;
  }

  font-size: 14px;

  border-radius: 8px;
  padding: 8px;
  position: relative;

  img {
    position: absolute;
    height: 20px;
    width: 20px;
  }
  margin-bottom: 4px;
  height: 34px;
  opacity: 0.7;

  box-sizing: border-box;
`;

const NewDot = styled('div')`
  height: 7px;
  width: 7px;
  border-radius: 100px;
  position: absolute;
  top: 5px;
  right: 5px;
  background: #ec4a77;
  z-index: 100;
`;

const NavItemWithoutLink = styled('div')<NavItemProps>`
  ${styledBase};
  color: var(--color-solid-white);

  &.active {
    opacity: 1 !important;
  }

  ${props =>
    props.minimized
      ? `
    padding: 0;
    width: 44px;
    height: 36px;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    border-radius: 4px;
    img { position: static; height: 20px; width: 20px; margin: 0; display: block; }
  `
      : 'img { position: absolute; left: 0; }'}

  ${props =>
    props.isTopLevel && !props.minimized
      ? `
    width: 226px;
    padding: 12px 4px 12px 4px;
    border-radius: 4px;
    /* default spacing when an icon is present */
    label { padding-left: 29px; }
    img { padding-left: 8px; width: 28px }
    /* when there's no icon, shift label/badges left to align */
    &[data-has-icon='false'] label { padding-left: 0px; }
    &[data-has-icon='false'] span > * { margin-left: 8px; }
    box-sizing: border-box;
    &:hover {
      background-color: ${SIDEBAR_HIGHLIGHT_COLOR};
    }
  `
      : ''}

    &.active {
    background-color: ${props =>
      props.isTopLevel && !props.minimized
        ? SIDEBAR_HIGHLIGHT_COLOR
        : props.theme.palette.colors.teal[700]};
  }
`;

const NavItem = styled(NavLink)<NavItemProps>`
  ${styledBase};
  color: var(--color-solid-white);
  display: flex;

  &.active {
    opacity: 1;
  }

  ${props =>
    props.minimized
      ? `
    padding: 0;
    width: 44px;
    height: 36px;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    border-radius: 4px;
    img { position: static; height: 20px; width: 20px; margin: 0; display: block; }
  `
      : 'img { position: absolute; left: 0; }'}

  ${props =>
    props.isTopLevel && !props.minimized
      ? `
    width: 226px;
    padding: 12px 4px 12px 4px;
    border-radius: 4px;
    label { padding-left: 28px; }
    img { padding-left: 8px; height: 16px; width: 24px }
    &[data-has-icon='false'] label { padding-left: 0px; }
    &[data-has-icon='false'] span > * { margin-left: 8px; }
    box-sizing: border-box;
    &:hover {
      background-color: ${SIDEBAR_HIGHLIGHT_COLOR};
    }
  `
      : ''}

  a {
    max-width: 50px;
  }

  &.active {
    background-color: ${props =>
      props.isTopLevel && !props.minimized
        ? SIDEBAR_HIGHLIGHT_COLOR
        : props.theme.palette.colors.teal[700]};
  }
`;

const Label = styled('label')`
  display: ${props => (props.hidden ? 'none' : 'block')};
  padding-left: 16px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
`;

const MemoizedNavMenuItem = memo(NavMenuItem);

export default MemoizedNavMenuItem;
